import { Button, Tabs } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import type { Connection, Person } from "../../../utils/backend-types";
import { collectHighlightedTabs } from "../../../utils/collectHighlightedTabs";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { CreatableFormFieldController } from "../../BuildingBlocks/Forms/Controllers/CreatableFormFieldController";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type { ConsumerResponse } from "../Consumer.types";
import { CONSUMER_TAB_VALUES } from "../ConsumerWizard.constants";
import { CONSUMER_FORM_FIELD_DATA } from "./ConsumerForm.constants";
import type { ConsumerFormData } from "./ConsumerForm.types";
import { ConsumerOperatorFormField } from "./ConsumerOperatorFormField/ConsumerOperatorFormField";

type ConsumerFormProps = {
  connections: Array<Connection>;
  persons: Array<Person>;
  showEligibleForTaxRelief?: boolean;
  showFooter?: boolean;
  showOperationalPurpose?: boolean;
  /** Preisdaten tab — shown only when the user may assess the site financially. */
  showPriceData?: boolean;
  onCancel: () => void;
  onChangeNumErrors?: (numErrors: number) => void;
  onChangeNumHighlightedFields?: (
    numHighlightedFields: number,
    highlightedTabs: Array<string>
  ) => void;
  onSubmitCreate: (
    data: ConsumerFormData
  ) => Promise<ApiResponse<ConsumerResponse>>;
  onSubmitUpdate: (
    data: ConsumerFormData,
    id: number
  ) => Promise<ApiResponse<ConsumerResponse>>;
} & WizardFormModeProps<ConsumerResponse>;

function ConsumerForm({
  connections,
  persons,
  showEligibleForTaxRelief = false,
  showFooter = true,
  showOperationalPurpose = false,
  showPriceData = false,
  onCancel,
  onChangeNumErrors,
  onChangeNumHighlightedFields,
  onSubmitCreate,
  onSubmitUpdate,
  ...modeProps
}: ConsumerFormProps) {
  const { showErrorToast } = useToast();
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch
  } = useForm<ConsumerFormData>({
    defaultValues:
      modeProps.mode === "edit"
        ? buildEditDefaultValues(
            modeProps.wizardObject,
            showOperationalPurpose,
            showEligibleForTaxRelief,
            showPriceData
          )
        : { electricity_price: null }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      CONSUMER_FORM_FIELD_DATA,
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );

  const isEdit = modeProps.mode === "edit";

  const personChoices = persons.map((person) => ({
    value: person.id,
    label: person.name
  }));

  const connectionChoices = connections.map((connection) => ({
    value: connection.id,
    label: connection.name
  }));

  useEffect(() => {
    onChangeNumErrors?.(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  const highlightedTabs = collectHighlightedTabs(formFieldData);
  const highlightedTabsKey = highlightedTabs.join(",");

  useEffect(() => {
    onChangeNumHighlightedFields?.(numHighlightedFields, highlightedTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- highlightedTabsKey is a stable string representation of highlightedTabs
  }, [numHighlightedFields, highlightedTabsKey, onChangeNumHighlightedFields]);

  const onValidSubmit: SubmitHandler<ConsumerFormData> = async (data) => {
    setIsSubmitting(true);

    try {
      return await (modeProps.mode === "create"
        ? onSubmitCreate(data)
        : onSubmitUpdate(data, modeProps.wizardObject.id));
    } catch (error) {
      setErrorsFromResponseData<ConsumerFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  const nameField = (
    <FormFieldController
      control={control}
      data={formFieldData.name}
      error={errors.name}
    />
  );
  const typeField = (
    <FormFieldController
      control={control}
      data={formFieldData.type}
      error={errors.type}
    />
  );
  const personSelectField = (
    <CreatableFormFieldController
      control={control}
      data={{ ...formFieldData.person, data: personChoices }}
      setValue={setValue}
      target="person"
    />
  );
  const connectionField = (
    <CreatableFormFieldController
      control={control}
      data={{ ...formFieldData.connection, data: connectionChoices }}
      setValue={setValue}
      target="connection"
    />
  );
  const operationalPurposeField = (
    <FormFieldController
      control={control}
      data={formFieldData.operational_purpose}
      error={errors.operational_purpose}
    />
  );
  const eligibleForTaxReliefField = (
    <FormFieldController
      control={control}
      data={formFieldData.eligible_for_tax_relief_for_particular_processes}
      error={errors.eligible_for_tax_relief_for_particular_processes}
    />
  );
  const electricityPriceField = (
    <FormFieldController
      control={control}
      data={formFieldData.electricity_price}
      error={errors.electricity_price}
    />
  );

  return (
    <form className="ConsumerForm" onSubmit={submitCallback}>
      {isEdit ? (
        <>
          <Tabs.Panel value={CONSUMER_TAB_VALUES.GENERAL}>
            {nameField}
            {typeField}
            {connectionField}
            {modeProps.mode === "edit" && (
              <ConsumerOperatorFormField
                consumerId={modeProps.wizardObject.id}
                control={control}
                personFieldData={formFieldData.person}
                persons={persons}
              />
            )}
          </Tabs.Panel>
          {(showOperationalPurpose || showEligibleForTaxRelief) && (
            <Tabs.Panel value={CONSUMER_TAB_VALUES.REGULATORY_DATA}>
              {showOperationalPurpose && operationalPurposeField}
              {showEligibleForTaxRelief && eligibleForTaxReliefField}
            </Tabs.Panel>
          )}
          {showPriceData && (
            <Tabs.Panel value={CONSUMER_TAB_VALUES.PRICE_DATA}>
              {electricityPriceField}
            </Tabs.Panel>
          )}
        </>
      ) : (
        <>
          {nameField}
          {typeField}
          {personSelectField}
          {connectionField}
          {electricityPriceField}
        </>
      )}

      {showFooter && (
        <ModalFooter>
          <Button
            disabled={isSubmitting}
            variant="default"
            onClick={handleClickCancel}
          >
            Abbrechen
          </Button>
          <Button
            color="secondary"
            loading={isSubmitting}
            onClick={submitCallback}
          >
            {isEdit ? "Speichern" : "Erstellen"}
          </Button>
        </ModalFooter>
      )}
    </form>
  );
}

/**
 * Maps a `ConsumerResponse` to the editable form fields. Regulatorische Daten and
 * Preisdaten fields are only seeded when they are shown, so that a hidden field
 * stays out of the form state — and therefore out of the PATCH payload.
 */
function buildEditDefaultValues(
  consumer: ConsumerResponse,
  showOperationalPurpose: boolean,
  showEligibleForTaxRelief: boolean,
  showPriceData: boolean
) {
  return {
    name: consumer.name,
    type: consumer.type,
    person: consumer.person ?? undefined,
    connection: consumer.connection,
    ...(showOperationalPurpose
      ? { operational_purpose: consumer.operational_purpose }
      : {}),
    ...(showEligibleForTaxRelief
      ? {
          eligible_for_tax_relief_for_particular_processes:
            consumer.eligible_for_tax_relief_for_particular_processes
        }
      : {}),
    ...(showPriceData ? { electricity_price: consumer.electricity_price } : {})
  };
}

export { ConsumerForm, ConsumerFormProps };
